/**
 * FEATURESPAGE.JS v10.0 - EIGENER SCROLL-CONTAINER
 *
 * ✅ v10.0: Eigener scrollbarer Container für React-App Integration
 *         - Scroll-Listener auf Container statt window
 *         - Funktioniert auch wenn App overflow:auto hat
 * ✅ v9.x: StoryTimeline integriert
 * ✅ v8.0: Nur 3 Planivio-Farben
 *
 * @version 10.0.0 - Eigener Scroll-Container
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../context/I18nContext';
import { usePublicPageView } from '../hooks/useAnalytics';

// ============================================================================
// PLANIVIO FARBEN - NUR DIESE 3!
// ============================================================================
const COLORS = {
  teal: '#4A90A4',           // ✅ Teal Primary
  softGreen: '#6BA893',      // ✅ Soft Green
  bg: '#E8F4F8',             // ✅ Teal Light Background

  // Abgeleitete Farben (nur Varianten der 3 Hauptfarben)
  gradient: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
  white: '#FFFFFF',
  darkText: '#2D3E50',       // Dunkler Text (aus Logo)
  text: '#4B5563',           // Normaler Text
  textLight: '#6B7280',      // Heller Text
  border: '#B8D4DC',         // Border (Teal-basiert)
};

// ============================================================================
// TIMELINE DATA - 8 Punkte für den Tagesablauf
// ============================================================================
const TIMELINE_POINTS = [
  { id: 'morning', icon: '🌅', time: '7:00', label: 'Morgen' },
  { id: 'midday', icon: '☀️', time: '12:00', label: 'Mittag' },
  { id: 'ai', icon: '🤖', time: '16:00', label: 'AI' },
  { id: 'tasks', icon: '✅', time: '16:30', label: 'Tasks' },
  { id: 'budget', icon: '💰', time: '17:00', label: 'Budget' },
  { id: 'shopping', icon: '🛒', time: '17:45', label: 'Einkauf' },
  { id: 'evening', icon: '📚', time: '19:00', label: 'Abend' },
  { id: 'night', icon: '🌙', time: '21:00', label: 'Nacht' },
];

// ============================================================================
// STORYTIMELINE KOMPONENTE - MIT ANIMATIONEN
// ============================================================================
function StoryTimeline({ scrollContainerRef }) {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const triggerY = 250;
      let currentActive = 0;

      for (let i = 0; i < TIMELINE_POINTS.length; i++) {
        const section = document.getElementById(TIMELINE_POINTS[i].id);
        if (section) {
          const rect = section.getBoundingClientRect();
          if (rect.top <= triggerY) {
            currentActive = i;
          }
        }
      }

      setActiveIndex(currentActive);
    };

    const scrollElement = scrollContainerRef?.current || window;
    setTimeout(handleScroll, 100);
    scrollElement.addEventListener('scroll', handleScroll);

    return () => {
      scrollElement.removeEventListener('scroll', handleScroll);
    };
  }, [scrollContainerRef]);

  const handlePointClick = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const progressPercent = (activeIndex / (TIMELINE_POINTS.length - 1)) * 100;

  return (
    <>
      {/* Timeline Animations CSS */}
      <style>{`
        @keyframes timelinePulse {
          0%, 100% {
            box-shadow: 0 4px 15px rgba(74, 144, 164, 0.4);
            transform: scale(1.1);
          }
          50% {
            box-shadow: 0 4px 25px rgba(74, 144, 164, 0.6), 0 0 0 8px rgba(74, 144, 164, 0.15);
            transform: scale(1.15);
          }
        }

        @keyframes timelineGlow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }

        @keyframes progressShine {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }

        .timeline-point-icon {
          transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .timeline-point-icon:hover {
          transform: scale(1.15) !important;
          box-shadow: 0 6px 20px rgba(74, 144, 164, 0.5) !important;
        }

        .timeline-point-icon.active {
          animation: timelinePulse 2s ease-in-out infinite;
        }

        .timeline-point-wrapper {
          transition: all 0.3s ease;
        }

        .timeline-point-wrapper:hover {
          transform: translateY(-4px);
        }

        .timeline-progress-line {
          background: linear-gradient(
            90deg,
            #4A90A4 0%,
            #6BA893 40%,
            #4A90A4 50%,
            #6BA893 60%,
            #4A90A4 100%
          );
          background-size: 200% 100%;
          animation: progressShine 3s linear infinite;
        }

        .timeline-time-label {
          transition: all 0.3s ease;
        }

        .timeline-point-wrapper:hover .timeline-time-label {
          transform: scale(1.1);
          color: #4A90A4 !important;
        }
      `}</style>

      <nav style={{
        position: 'sticky',
        top: '53px',
        background: 'rgba(255, 255, 255, 0.98)',
        backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${COLORS.border}`,
        zIndex: 999,
        padding: '16px 24px',
        overflowX: 'auto',
        boxShadow: '0 2px 10px rgba(74, 144, 164, 0.1)'
      }}>
        <div style={{
          maxWidth: '1000px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
          minWidth: 'max-content',
          position: 'relative'
        }}>
          {/* Basis-Linie (grau) */}
          <div style={{
            position: 'absolute',
            top: '22px',
            left: '30px',
            right: '30px',
            height: '3px',
            background: COLORS.border,
            borderRadius: '2px',
            zIndex: 0
          }} />

          {/* Fortschritts-Linie (animiert) */}
          <div
            className="timeline-progress-line"
            style={{
              position: 'absolute',
              top: '21px',
              left: '30px',
              height: '5px',
              borderRadius: '3px',
              zIndex: 1,
              transition: 'width 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
              width: `${progressPercent * 0.94}%`
            }}
          />

          {/* Timeline Punkte */}
          {TIMELINE_POINTS.map((point, index) => {
            const isPassed = index < activeIndex;
            const isActive = index === activeIndex;

            return (
              <div
                key={point.id}
                className="timeline-point-wrapper"
                onClick={() => handlePointClick(point.id)}
                title={`${point.time} - ${point.label}`}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                  cursor: 'pointer',
                  padding: '8px',
                  position: 'relative',
                  zIndex: 2
                }}
              >
                <div
                  className={`timeline-point-icon ${isActive ? 'active' : ''}`}
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.2rem',
                    background: isActive ? COLORS.gradient : (isPassed ? COLORS.bg : COLORS.white),
                    border: `3px solid ${(isActive || isPassed) ? COLORS.teal : COLORS.border}`,
                    boxShadow: isActive
                      ? '0 4px 15px rgba(74, 144, 164, 0.4)'
                      : isPassed
                        ? '0 2px 10px rgba(74, 144, 164, 0.2)'
                        : '0 2px 8px rgba(0, 0, 0, 0.05)',
                    transform: isActive ? 'scale(1.1)' : 'scale(1)'
                  }}
                >
                  {point.icon}
                </div>
                <div
                  className="timeline-time-label"
                  style={{
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: (isActive || isPassed) ? COLORS.teal : COLORS.textLight
                  }}
                >
                  {point.time}
                </div>
              </div>
            );
          })}
        </div>
      </nav>
    </>
  );
}

// ============================================================================
// MAIN COMPONENT - FeaturesPage
// ============================================================================
function FeaturesPage({ onGetStarted, onNavigate }) {
  usePublicPageView('features');
  useTranslation();
  const scrollContainerRef = useRef(null);

  return (
    <div 
      ref={scrollContainerRef}
      style={{ 
        fontFamily: "'Inter', sans-serif", 
        background: COLORS.white, 
        color: COLORS.darkText, 
        lineHeight: 1.7, 
        overflow: 'auto',  // WICHTIG: Eigener Scroll-Container!
        height: '100vh',   // Volle Höhe
        position: 'relative'
      }}
    >

      {/* CSS */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

        * { margin: 0; padding: 0; box-sizing: border-box; }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
        }

        @keyframes bounce {
          0%, 100% { transform: translateX(-50%) translateY(0); }
          50% { transform: translateX(-50%) translateY(10px); }
        }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .hero-badge .dot {
          animation: pulse 2s ease-in-out infinite;
        }

        .scroll-hint {
          animation: bounce 2s ease-in-out infinite;
        }

        .chaos-card {
          animation: fadeInUp 0.6s ease-out both;
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(74, 144, 164, 0.4);
        }

        .btn-secondary:hover {
          border-color: ${COLORS.teal};
          color: ${COLORS.teal};
        }

        .btn-white:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(74, 144, 164, 0.3);
        }

        .chaos-card:hover .chaos-card-inner {
          background: rgba(255, 255, 255, 0.25);
          border-color: rgba(255, 255, 255, 0.4);
          transform: translateY(-4px);
        }

        .timeline-point:hover {
          transform: scale(1.1);
        }

        @media (max-width: 900px) {
          .story-content { grid-template-columns: 1fr !important; gap: 40px !important; }
          .chaos-cards-container { grid-template-columns: 1fr !important; }
        }

        @media (max-width: 600px) {
          .hero h1 { font-size: 2rem !important; }
          .story-text h2 { font-size: 1.5rem !important; }
          .hero-cta { flex-direction: column; align-items: center; }
        }
      `}</style>

      {/* ============ BACK TO LANDING NAV ============ */}
      <nav style={{
        position: 'sticky',
        top: 0,
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${COLORS.border}`,
        zIndex: 1001,
        padding: '12px 24px'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <button
            onClick={() => onNavigate && onNavigate('landing')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              background: COLORS.bg,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              color: COLORS.teal,
              fontSize: '0.9rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            ← Zurück zur Startseite
          </button>
          <img src="/images/Logos/planivio_logo_primary_400px.png" alt="Planivio" style={{ height: '32px', width: 'auto' }} />
          <button
            onClick={onGetStarted}
            style={{
              padding: '10px 20px',
              background: COLORS.gradient,
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 2px 10px rgba(74, 144, 164, 0.3)'
            }}
          >
            Beta testen
          </button>
        </div>
      </nav>

      {/* ============ HERO ============ */}
      <section style={{
        minHeight: 'calc(100vh - 60px)',
        background: `linear-gradient(180deg, ${COLORS.bg} 0%, ${COLORS.white} 100%)`,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        padding: '60px 24px',
        position: 'relative'
      }}>
        <div className="hero-badge" style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 16px',
          background: COLORS.white,
          border: `1px solid ${COLORS.border}`,
          borderRadius: '100px',
          fontSize: '0.85rem',
          color: COLORS.text,
          marginBottom: '24px',
          boxShadow: '0 2px 10px rgba(74, 144, 164, 0.1)'
        }}>
          <span className="dot" style={{
            width: '8px',
            height: '8px',
            background: COLORS.teal,
            borderRadius: '50%'
          }}></span>
          Ein Tag mit Familie Müller
        </div>

        <h1 style={{
          fontSize: 'clamp(2.5rem, 6vw, 4rem)',
          fontWeight: 900,
          lineHeight: 1.1,
          marginBottom: '24px',
          letterSpacing: '-0.02em',
          maxWidth: '800px'
        }}>
          Weniger Chaos.<br/>
          <span style={{
            background: COLORS.gradient,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>Mehr Familie.</span>
        </h1>

        <p style={{
          fontSize: '1.2rem',
          color: COLORS.text,
          maxWidth: '600px',
          margin: '0 auto 32px'
        }}>
          5-7 Apps. Endlose WhatsApp-Gruppen. Zettel auf dem Kühlschrank.<br/>
          <strong>Das muss nicht sein.</strong>
        </p>

        <div className="hero-cta" style={{
          display: 'flex',
          gap: '16px',
          justifyContent: 'center',
          flexWrap: 'wrap',
          marginBottom: '40px'
        }}>
          <button onClick={() => document.getElementById('chaos')?.scrollIntoView({ behavior: 'smooth' })} style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '16px 32px',
            background: COLORS.gradient,
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(74, 144, 164, 0.3)',
            transition: 'all 0.3s ease'
          }} className="btn-primary">
            Die Geschichte erleben →
          </button>
          <button onClick={onGetStarted} style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '16px 32px',
            background: COLORS.white,
            color: COLORS.darkText,
            border: `2px solid ${COLORS.border}`,
            borderRadius: '12px',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }} className="btn-secondary">
            Direkt starten
          </button>
        </div>

        <div className="scroll-hint" style={{
          position: 'absolute',
          bottom: '40px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px',
          color: COLORS.textLight,
          fontSize: '0.85rem'
        }}>
          <span>Scroll für die Story</span>
          <span>↓</span>
        </div>
      </section>

      {/* ============ CHAOS IMMERSIVE ============ */}
      <section id="chaos" style={{
        minHeight: '100vh',
        background: `linear-gradient(180deg, ${COLORS.teal} 0%, ${COLORS.softGreen} 50%, ${COLORS.bg} 100%)`,
        position: 'relative',
        overflow: 'hidden',
        padding: '100px 24px'
      }}>
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.08) 0%, transparent 50%)',
          pointerEvents: 'none'
        }}></div>

        <div style={{ maxWidth: '1000px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <span style={{
              display: 'inline-block',
              padding: '8px 20px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '100px',
              color: COLORS.white,
              fontSize: '0.9rem',
              fontWeight: 600,
              marginBottom: '20px',
              letterSpacing: '0.05em'
            }}>Jeden. Single. Tag.</span>
            <h2 style={{
              fontSize: 'clamp(2.5rem, 6vw, 4rem)',
              fontWeight: 900,
              color: 'white',
              letterSpacing: '-0.02em'
            }}>Kennst du das?</h2>
          </div>

          <div className="chaos-cards-container" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '20px',
            marginBottom: '80px'
          }}>
            {[
              { icon: '😫', quote: '"Was muss ich heute alles erledigen?"', subtext: 'Noch bevor die Augen richtig offen sind', delay: '0s' },
              { icon: '🔥', quote: '"MAMA! Wo ist mein Sportzeug?!"', subtext: 'Alle wollen gleichzeitig etwas', delay: '0.1s' },
              { icon: '📱', quote: '"Scroll mal hoch, da stand\'s doch!"', subtext: '5 WhatsApp-Gruppen. Keine Übersicht.', delay: '0.2s' },
              { icon: '🔍', quote: '"Hat jemand meinen Schlüssel gesehen?"', subtext: 'Die ewige Suche. Jeden Morgen.', delay: '0.3s' },
              { icon: '😔', quote: '"Hab ich irgendwas vergessen?"', subtext: 'Erschöpft. Und der Tag hat noch nicht begonnen.', delay: '0.4s' }
            ].map((card, i) => (
              <div key={i} className="chaos-card" style={{ animationDelay: card.delay }}>
                <div className="chaos-card-inner" style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  borderRadius: '20px',
                  padding: '28px',
                  height: '100%',
                  transition: 'all 0.4s ease',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: '16px' }}>{card.icon}</div>
                  <div style={{ fontSize: '1.15rem', fontWeight: 600, color: 'white', lineHeight: 1.4, marginBottom: '12px' }}>{card.quote}</div>
                  <div style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>{card.subtext}</div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '24px' }}>
              <span style={{ fontSize: '1.2rem', color: 'rgba(255, 255, 255, 0.85)' }}>Das ist</span>
              <strong style={{ fontSize: '1.5rem', fontWeight: 800, color: COLORS.white }}>Mental Load</strong>
            </div>
            <div style={{ width: '60px', height: '2px', background: 'rgba(255, 255, 255, 0.5)', margin: '0 auto 24px' }}></div>
            <div style={{
              fontSize: '1.8rem',
              fontWeight: 700,
              color: COLORS.white
            }}>Es geht auch anders.</div>
          </div>
        </div>
      </section>

      {/* ============ COMPARISON SECTION - Der Unterschied auf einen Blick ============ */}
      <section style={{
        padding: '80px 24px',
        background: COLORS.white
      }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <h2 style={{ fontSize: '2rem', fontWeight: 800, color: COLORS.darkText, marginBottom: '16px' }}>
              Der Unterschied auf einen Blick
            </h2>
            <p style={{ color: COLORS.text, maxWidth: '600px', margin: '0 auto' }}>
              Wie sich dein Alltag mit PLANIVIO verändert
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px',
            maxWidth: '900px',
            margin: '0 auto'
          }}>
            {/* Ohne PLANIVIO */}
            <div style={{
              background: '#FEE2E2',
              borderRadius: '16px',
              padding: '24px',
              border: '2px solid #FECACA'
            }}>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 700,
                color: '#EF4444',
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                ❌ Ohne PLANIVIO
              </h3>
              {[
                { icon: '😫', text: '5-7 verschiedene Apps' },
                { icon: '📱', text: 'Endlose WhatsApp-Gruppen' },
                { icon: '📝', text: 'Zettel auf dem Kühlschrank' },
                { icon: '🤯', text: 'Mental Load bei einer Person' },
                { icon: '❓', text: '"Haben wir noch...?"' },
                { icon: '💸', text: 'Kein Überblick über Ausgaben' }
              ].map((item, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px 0',
                  borderBottom: i < 5 ? '1px solid #FECACA' : 'none'
                }}>
                  <span style={{ fontSize: '1.25rem' }}>{item.icon}</span>
                  <span style={{ color: COLORS.darkText }}>{item.text}</span>
                </div>
              ))}
            </div>

            {/* Mit PLANIVIO */}
            <div style={{
              background: '#D1FAE5',
              borderRadius: '16px',
              padding: '24px',
              border: '2px solid #A7F3D0'
            }}>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 700,
                color: '#10B981',
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                ✅ Mit PLANIVIO
              </h3>
              {[
                { icon: '✨', text: 'Eine App für alles' },
                { icon: '🔄', text: 'Echtzeit-Sync für alle' },
                { icon: '📱', text: 'Digital & immer dabei' },
                { icon: '⚖️', text: 'Faire Aufgabenverteilung' },
                { icon: '✅', text: 'Alles auf einen Blick' },
                { icon: '📊', text: 'Budget im Griff' }
              ].map((item, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px 0',
                  borderBottom: i < 5 ? '1px solid #A7F3D0' : 'none'
                }}>
                  <span style={{ fontSize: '1.25rem' }}>{item.icon}</span>
                  <span style={{ color: COLORS.darkText }}>{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ TIMELINE NAV - Integrierte Komponente ============ */}
      <StoryTimeline scrollContainerRef={scrollContainerRef} />

      {/* ============ STORY SECTIONS - Alle mit Teal-Farben ============ */}

      {/* MORNING */}
      <section id="morning" style={{
        padding: '100px 24px',
        background: `linear-gradient(180deg, ${COLORS.bg} 0%, ${COLORS.white} 100%)`
      }}>
        <div className="story-content" style={{
          maxWidth: '1100px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '60px',
          alignItems: 'center'
        }}>
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '100px', fontSize: '0.9rem', fontWeight: 600, marginBottom: '16px', background: COLORS.bg, color: COLORS.teal, border: `1px solid ${COLORS.border}` }}>
              🌅 7:15 Uhr – Mit PLANIVIO
            </div>
            <h2 style={{ fontSize: '2rem', fontWeight: 800, marginBottom: '20px', lineHeight: 1.2, color: COLORS.darkText }}>Ein Blick. Alles klar.</h2>
            <div style={{ fontSize: '1.05rem', color: COLORS.text, marginBottom: '24px', padding: '20px', background: COLORS.bg, borderRadius: '12px', borderLeft: `4px solid ${COLORS.teal}` }}>
              Lisa öffnet PLANIVIO. <strong>In 5 Sekunden</strong> weiß sie:<br/><br/>
              • Heute Abend: <strong>Elternabend Emma</strong> (19:00)<br/>
              • Max' Training: <strong>abgesagt</strong> (40 Min Fahrt gespart!)<br/>
              • Einkaufsliste: <strong>3 Items</strong> warten<br/><br/>
              Kein Suchen. Kein Raten. Kein Stress.
            </div>
            <FeatureBox icon="📊" title="Dashboard" description="Dein Morgen-Briefing. Alles Wichtige auf einen Blick." />
            <CTABox text="Nie wieder Chaos-Morgen?" onClick={onGetStarted} />
          </div>
          <div>
            <PhoneMockup
              headerTitle="Guten Morgen, Lisa! ☀️"
              headerSubtitle="Dienstag, 21. Januar"
              cards={[
                { icon: '📅', title: 'Elternabend Emma', subtitle: '19:00 Uhr • Gymnasium' },
                { icon: '⚽', title: 'Training abgesagt', subtitle: 'Max • Trainer krank' },
                { icon: '🛒', title: '3 Items auf der Liste', subtitle: 'Milch, Brot, Käse' }
              ]}
            />
          </div>
        </div>
      </section>

      {/* MIDDAY */}
      <section id="midday" style={{
        padding: '100px 24px',
        background: COLORS.white
      }}>
        <div className="story-content" style={{
          maxWidth: '1100px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '60px',
          alignItems: 'center'
        }}>
          <div>
            <PhoneMockup
              headerTitle="🍳 Rezepte"
              headerSubtitle="Familiensammlung"
              cards={[
                { icon: '🥔', title: 'Omas Kartoffelsuppe', subtitle: '⭐ Familien-Favorit • 45 Min' }
              ]}
              footer={{ text: '✓ 6 Zutaten zur Liste hinzugefügt' }}
            />
          </div>
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '100px', fontSize: '0.9rem', fontWeight: 600, marginBottom: '16px', background: COLORS.bg, color: COLORS.teal, border: `1px solid ${COLORS.border}` }}>
              ☀️ 12:00 Uhr – Büro
            </div>
            <h2 style={{ fontSize: '2rem', fontWeight: 800, marginBottom: '20px', lineHeight: 1.2, color: COLORS.darkText }}>"Was kochen wir heute?"</h2>
            <div style={{ fontSize: '1.05rem', color: COLORS.text, marginBottom: '24px', padding: '20px', background: COLORS.bg, borderRadius: '12px', borderLeft: `4px solid ${COLORS.teal}` }}>
              Thomas schreibt: <strong>"Was kochen wir?"</strong><br/><br/>
              Früher: 10 Minuten Ping-Pong. <em>"Keine Ahnung." "Was haben wir da?"</em><br/><br/>
              Heute: Lisa tippt "Kartoffelsuppe". <strong>Ein Klick</strong> – alle Zutaten auf der Liste.<br/><br/>
              <strong>30 Sekunden statt 10 Minuten.</strong>
            </div>
            <FeatureBox icon="🍳" title="Rezepte → Einkaufsliste" description="Ein Klick. Alle Zutaten. Keine vergessenen Items." />
          </div>
        </div>
      </section>

      {/* AI */}
      <section id="ai" style={{
        padding: '100px 24px',
        background: `linear-gradient(180deg, ${COLORS.bg} 0%, ${COLORS.white} 100%)`
      }}>
        <div className="story-content" style={{
          maxWidth: '1100px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '60px',
          alignItems: 'center'
        }}>
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '100px', fontSize: '0.9rem', fontWeight: 600, marginBottom: '16px', background: COLORS.bg, color: COLORS.teal, border: `1px solid ${COLORS.border}` }}>
              🤖 16:00 Uhr – AI hilft
            </div>
            <h2 style={{ fontSize: '2rem', fontWeight: 800, marginBottom: '20px', lineHeight: 1.2, color: COLORS.darkText }}>Keine Idee? AI hat welche.</h2>
            <div style={{ fontSize: '1.05rem', color: COLORS.text, marginBottom: '24px', padding: '20px', background: COLORS.bg, borderRadius: '12px', borderLeft: `4px solid ${COLORS.teal}` }}>
              Lisa schaut in den Kühlschrank: Kartoffeln, Lauch, Sahne. Uninspiriert.<br/><br/>
              Sie fragt die <strong>PLANIVIO AI</strong>: "Was kann ich damit kochen?"<br/><br/>
              <strong>Sekunden später:</strong><br/>
              1. Kartoffel-Lauch-Gratin (45 Min)<br/>
              2. Lauch-Kartoffel-Suppe (30 Min)<br/>
              3. Überbackene Kartoffeln (35 Min)<br/><br/>
              <strong>"Perfekt. Abendessen gelöst."</strong>
            </div>
            <FeatureBox icon="🤖" title="AI-Assistent" description="Rezeptideen basierend auf dem, was du hast." />
          </div>
          <div>
            <PhoneMockup
              headerTitle="🤖 AI-Assistent"
              headerSubtitle="Rezeptvorschläge"
              customContent={
                <>
                  <div style={{ background: COLORS.bg, padding: '12px', borderRadius: '10px', fontSize: '0.8rem', marginBottom: '12px' }}>
                    <strong>Du:</strong> "Kartoffeln, Lauch, Sahne"
                  </div>
                  <MiniCard icon="1" title="Kartoffel-Lauch-Gratin" subtitle="Herzhaft • 45 Min" />
                  <MiniCard icon="2" title="Lauch-Kartoffel-Suppe" subtitle="Schnell • 30 Min" />
                </>
              }
            />
          </div>
        </div>
      </section>

      {/* TASKS */}
      <section id="tasks" style={{
        padding: '100px 24px',
        background: COLORS.white
      }}>
        <div className="story-content" style={{
          maxWidth: '1100px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '60px',
          alignItems: 'center'
        }}>
          <div>
            <PhoneMockup
              headerTitle="✅ Aufgaben"
              headerSubtitle="Haushalt – Diese Woche"
              cards={[
                { icon: '✓', title: 'Wäsche waschen', subtitle: '👩 Lisa • erledigt', done: true },
                { icon: '✓', title: 'Müll rausbringen', subtitle: '👨 Thomas • erledigt', done: true },
                { icon: '☐', title: 'Staubsaugen', subtitle: '👧 Emma • bis Mittwoch' }
              ]}
            />
          </div>
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '100px', fontSize: '0.9rem', fontWeight: 600, marginBottom: '16px', background: COLORS.bg, color: COLORS.teal, border: `1px solid ${COLORS.border}` }}>
              ✅ 16:30 Uhr – Mental Load
            </div>
            <h2 style={{ fontSize: '2rem', fontWeight: 800, marginBottom: '20px', lineHeight: 1.2, color: COLORS.darkText }}>Die unsichtbare Arbeit sichtbar.</h2>
            <div style={{ fontSize: '1.05rem', color: COLORS.text, marginBottom: '24px', padding: '20px', background: COLORS.bg, borderRadius: '12px', borderLeft: `4px solid ${COLORS.teal}` }}>
              Lisa öffnet das <strong>Tasks-Modul</strong>. Dort sieht sie:<br/><br/>
              ✓ Lisa: Wäsche, Kochen (4x), Arzttermin<br/>
              ✓ Thomas: Einkauf (2x), Müll, Auto TÜV<br/><br/>
              Früher war das alles <strong>in Lisas Kopf</strong>.<br/><br/>
              Jetzt sieht Thomas: <em>"Oh, du hast viel gemacht. Ich übernehme mehr."</em><br/><br/>
              <strong>Mental Load geteilt = Mental Load halbiert.</strong>
            </div>
            <FeatureBox icon="✅" title="Tasks-Modul" description="Aufgaben zuweisen, abhaken, Fortschritt sehen." />
          </div>
        </div>
      </section>

      {/* BUDGET */}
      <section id="budget" style={{
        padding: '100px 24px',
        background: `linear-gradient(180deg, ${COLORS.bg} 0%, ${COLORS.white} 100%)`
      }}>
        <div className="story-content" style={{
          maxWidth: '1100px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '60px',
          alignItems: 'center'
        }}>
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '100px', fontSize: '0.9rem', fontWeight: 600, marginBottom: '16px', background: COLORS.bg, color: COLORS.teal, border: `1px solid ${COLORS.border}` }}>
              💰 17:00 Uhr – Budget-Check
            </div>
            <h2 style={{ fontSize: '2rem', fontWeight: 800, marginBottom: '20px', lineHeight: 1.2, color: COLORS.darkText }}>Wo geht das Geld hin?</h2>
            <div style={{ fontSize: '1.05rem', color: COLORS.text, marginBottom: '24px', padding: '20px', background: COLORS.bg, borderRadius: '12px', borderLeft: `4px solid ${COLORS.teal}` }}>
              Lisa checkt das <strong>Budget-Modul</strong>. Auf einen Blick:<br/><br/>
              📊 Januar: 4.200€ Einnahmen, 3.150€ Ausgaben<br/>
              🛒 Lebensmittel: 680€ (Budget: 700€) ✅<br/>
              🚗 Auto: 320€ (Budget: 300€) ⚠️<br/><br/>
              <strong>Zum ersten Mal wissen beide, wo das Geld hingeht.</strong><br/><br/>
              Keine Überraschungen mehr am Monatsende.
            </div>
            <FeatureBox icon="💰" title="Budget-Modul" description="Ausgaben tracken. Budgets setzen. Überblick behalten." />
          </div>
          <div>
            <PhoneMockup
              headerTitle="💰 Budget"
              headerSubtitle="Januar 2026"
              customContent={
                <>
                  <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <div style={{ fontSize: '2rem', fontWeight: 800, color: COLORS.softGreen }}>1.050 €</div>
                    <div style={{ fontSize: '0.8rem', color: COLORS.textLight }}>Übrig diesen Monat</div>
                  </div>
                  <MiniCard icon="🛒" title="Lebensmittel" subtitle="680€ / 700€ ✓" subtitleColor={COLORS.softGreen} />
                  <MiniCard icon="🚗" title="Auto" subtitle="320€ / 300€ ⚠️" subtitleColor={COLORS.teal} />
                </>
              }
            />
          </div>
        </div>
      </section>

      {/* SHOPPING */}
      <section id="shopping" style={{
        padding: '100px 24px',
        background: COLORS.white
      }}>
        <div className="story-content" style={{
          maxWidth: '1100px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '60px',
          alignItems: 'center'
        }}>
          <div>
            <PhoneMockup
              headerTitle="🛒 Wocheneinkauf"
              headerSubtitle="Thomas kauft gerade ein..."
              cards={[
                { icon: '✓', title: 'Milch (Hafer, 1L)', subtitle: '👩 Lisa • erledigt', done: true },
                { icon: '🥔', title: 'Kartoffeln 2kg', subtitle: 'Aus Rezept' },
                { icon: '📓', title: 'Matheheft kariert', subtitle: '👧 Emma' }
              ]}
            />
          </div>
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '100px', fontSize: '0.9rem', fontWeight: 600, marginBottom: '16px', background: COLORS.bg, color: COLORS.teal, border: `1px solid ${COLORS.border}` }}>
              🛒 17:45 Uhr – Supermarkt
            </div>
            <h2 style={{ fontSize: '2rem', fontWeight: 800, marginBottom: '20px', lineHeight: 1.2, color: COLORS.darkText }}>Der Einkauf ohne Rückfragen.</h2>
            <div style={{ fontSize: '1.05rem', color: COLORS.text, marginBottom: '24px', padding: '20px', background: COLORS.bg, borderRadius: '12px', borderLeft: `4px solid ${COLORS.teal}` }}>
              Thomas steht im Laden. Die Liste zeigt:<br/><br/>
              ☐ Milch (Hafer, 1L) – von Lisa<br/>
              ☐ Kartoffeln – aus Rezept<br/>
              ☐ Matheheft – von Emma<br/><br/>
              Er hakt ab. <strong>Lisa sieht den Fortschritt zuhause.</strong><br/><br/>
              Keine "Bist du noch im Laden?"-Nachrichten.<br/>
              Die Kinder haben selbst Items hinzugefügt.<br/><br/>
              <strong>Eine Familie. Eine Liste.</strong>
            </div>
            <FeatureBox icon="🛒" title="Geteilte Einkaufslisten" description="Echtzeit-Sync. Jeder sieht, was fehlt." />
          </div>
        </div>
      </section>

      {/* EVENING */}
      <section id="evening" style={{
        padding: '100px 24px',
        background: `linear-gradient(180deg, ${COLORS.bg} 0%, ${COLORS.white} 100%)`
      }}>
        <div className="story-content" style={{
          maxWidth: '1100px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '60px',
          alignItems: 'center'
        }}>
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '100px', fontSize: '0.9rem', fontWeight: 600, marginBottom: '16px', background: COLORS.bg, color: COLORS.teal, border: `1px solid ${COLORS.border}` }}>
              📚 19:00 Uhr – Elternabend
            </div>
            <h2 style={{ fontSize: '2rem', fontWeight: 800, marginBottom: '20px', lineHeight: 1.2, color: COLORS.darkText }}>Vorbereitet. Ohne Aufwand.</h2>
            <div style={{ fontSize: '1.05rem', color: COLORS.text, marginBottom: '24px', padding: '20px', background: COLORS.bg, borderRadius: '12px', borderLeft: `4px solid ${COLORS.teal}` }}>
              Lisa öffnet die geteilte Notiz <strong>"Emma – Schule"</strong>:<br/><br/>
              • Lehrerin: Frau Schneider<br/>
              • Emma braucht Hilfe bei Bruchrechnung<br/>
              • Klassenfahrt März – Formulare unterschreiben!<br/><br/>
              Sie ist vorbereitet. Nicht weil sie alles im Kopf hat.<br/><br/>
              <strong>Sondern weil die Familie gemeinsam dokumentiert.</strong>
            </div>
            <FeatureBox icon="📝" title="Notes-Modul" description="Geteilte Notizen. Immer aktuell. Immer griffbereit." />
          </div>
          <div>
            <PhoneMockup
              headerTitle="📝 Notizen"
              headerSubtitle="Emma – Schule"
              customContent={
                <>
                  <div style={{ fontSize: '0.75rem', color: COLORS.textLight, marginBottom: '12px' }}>
                    📌 Angepinnt • Geteilt mit Thomas
                  </div>
                  <MiniCard title="Lehrerin:" subtitle="Frau Schneider" />
                  <div style={{ background: COLORS.bg, borderRadius: '10px', padding: '12px', marginBottom: '10px', fontSize: '0.85rem', border: `1px solid ${COLORS.border}` }}>
                    ⚠️ <strong>Bruchrechnung</strong> – braucht Hilfe
                  </div>
                  <div style={{ background: COLORS.bg, borderRadius: '10px', padding: '12px', fontSize: '0.85rem', border: `1px solid ${COLORS.border}` }}>
                    📋 <strong>Klassenfahrt März</strong><br/>
                    <span style={{ fontSize: '0.75rem' }}>Formulare unterschreiben!</span>
                  </div>
                </>
              }
            />
          </div>
        </div>
      </section>

      {/* NIGHT */}
      <section id="night" style={{
        padding: '100px 24px',
        background: COLORS.gradient,
        color: COLORS.white
      }}>
        <div className="story-content" style={{
          maxWidth: '1100px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '60px',
          alignItems: 'center'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '6rem', marginBottom: '24px' }}>🛋️</div>
            <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '1.1rem', maxWidth: '300px', margin: '0 auto', fontStyle: 'italic' }}>
              "Es geht nicht um perfekte Organisation. Es geht um mehr Zeit für das, was wirklich zählt."
            </p>
          </div>
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '100px', fontSize: '0.9rem', fontWeight: 600, marginBottom: '16px', background: 'rgba(255,255,255,0.2)', color: 'white' }}>
              🌙 21:00 Uhr – Feierabend
            </div>
            <h2 style={{ fontSize: '2rem', fontWeight: 800, marginBottom: '20px', lineHeight: 1.2 }}>Das war's für heute.</h2>
            <div style={{ fontSize: '1.05rem', color: 'rgba(255,255,255,0.9)', marginBottom: '24px', padding: '20px', background: 'rgba(255,255,255,0.1)', borderRadius: '12px', borderLeft: '4px solid rgba(255,255,255,0.5)' }}>
              Die Kinder sind im Bett. Lisa und Thomas sitzen auf dem Sofa.<br/><br/>
              <strong>Kein "Haben wir eigentlich..."</strong><br/>
              <strong>Kein "Wer macht morgen..."</strong><br/><br/>
              Alles ist geregelt. Alles ist geteilt.<br/><br/>
              <em>"Weißt du was? Heute war ein guter Tag."</em>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '20px', background: 'rgba(255,255,255,0.1)', border: '2px solid rgba(255,255,255,0.3)', borderRadius: '16px' }}>
              <div style={{ width: '56px', height: '56px', borderRadius: '12px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', flexShrink: 0 }}>📅</div>
              <div>
                <h4 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '4px', color: 'white' }}>Familienkalender</h4>
                <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)', margin: 0 }}>Morgen um 7:15 beginnt ein neuer Tag. Ohne Chaos.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CTA SECTION ============ */}
      <section style={{
        background: COLORS.bg,
        padding: '100px 24px',
        textAlign: 'center'
      }}>
        <h2 style={{ fontSize: 'clamp(2rem, 4vw, 2.5rem)', fontWeight: 800, marginBottom: '16px', color: COLORS.darkText }}>
          Bereit für weniger Chaos?
        </h2>
        <p style={{ fontSize: '1.1rem', color: COLORS.text, marginBottom: '32px', maxWidth: '500px', marginLeft: 'auto', marginRight: 'auto' }}>
          Starte jetzt kostenlos und erlebe, wie sich dein Familienalltag verändert.
        </p>
        <button onClick={onGetStarted} className="btn-white" style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '18px 40px',
          background: COLORS.gradient,
          color: COLORS.white,
          border: 'none',
          borderRadius: '12px',
          fontSize: '1.1rem',
          fontWeight: 700,
          cursor: 'pointer',
          boxShadow: '0 4px 20px rgba(74, 144, 164, 0.3)',
          transition: 'all 0.3s ease'
        }}>
          Kostenlos starten →
        </button>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', marginTop: '24px', flexWrap: 'wrap', fontSize: '0.85rem', color: COLORS.text }}>
          <span>✓ Keine Kreditkarte</span>
          <span>✓ DSGVO-konform</span>
          <span>✓ Made in Germany 🇩🇪</span>
        </div>
      </section>

      {/* ============ FOOTER (kompakt) ============ */}
      <footer style={{
        padding: '24px',
        background: COLORS.gradient,
        color: COLORS.white,
        textAlign: 'center'
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', flexWrap: 'wrap', fontSize: '0.8rem' }}>
          <img src="/images/Logos/planivio_logo_white_400px.png" alt="Planivio" style={{ height: '24px', width: 'auto' }} />
          <span style={{ opacity: 0.6 }}>|</span>
          <button onClick={() => onNavigate && onNavigate('landing')} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.9)', fontSize: '0.8rem', cursor: 'pointer' }}>Startseite</button>
          <button onClick={() => onNavigate && onNavigate('datenschutz')} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', cursor: 'pointer' }}>Datenschutz</button>
          <button onClick={() => onNavigate && onNavigate('impressum')} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', cursor: 'pointer' }}>Impressum</button>
          <span style={{ opacity: 0.6 }}>|</span>
          <span style={{ opacity: 0.6 }}>© 2026</span>
        </div>
      </footer>
    </div>
  );
}

// ============================================================================
// HELPER COMPONENTS - Alle mit Planivio Farben
// ============================================================================

function FeatureBox({ icon, title, description }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      padding: '20px',
      background: COLORS.white,
      border: `2px solid ${COLORS.teal}`,
      borderRadius: '16px',
      marginBottom: '20px'
    }}>
      <div style={{
        width: '56px',
        height: '56px',
        borderRadius: '12px',
        background: COLORS.gradient,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.5rem',
        flexShrink: 0
      }}>{icon}</div>
      <div>
        <h4 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '4px', color: COLORS.darkText }}>{title}</h4>
        <p style={{ fontSize: '0.9rem', color: COLORS.textLight, margin: 0 }}>{description}</p>
      </div>
    </div>
  );
}

function CTABox({ text, onClick }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      padding: '16px 20px',
      background: COLORS.bg,
      borderRadius: '12px',
      border: `1px solid ${COLORS.border}`
    }}>
      <p style={{ flex: 1, fontSize: '0.95rem', color: COLORS.darkText, margin: 0 }}><strong>{text}</strong></p>
      <button onClick={onClick} className="btn-primary" style={{
        padding: '10px 20px',
        fontSize: '0.9rem',
        background: COLORS.gradient,
        color: 'white',
        border: 'none',
        borderRadius: '12px',
        fontWeight: 600,
        cursor: 'pointer'
      }}>Jetzt testen</button>
    </div>
  );
}

function PhoneMockup({ headerTitle, headerSubtitle, cards = [], footer, customContent }) {
  return (
    <div style={{
      width: '100%',
      maxWidth: '300px',
      margin: '0 auto',
      background: COLORS.teal,
      borderRadius: '36px',
      padding: '10px',
      boxShadow: '0 40px 80px rgba(74, 144, 164, 0.3)'
    }}>
      <div style={{
        background: COLORS.white,
        borderRadius: '28px',
        overflow: 'hidden'
      }}>
        <div style={{
          background: COLORS.gradient,
          padding: '32px 16px 16px',
          color: 'white'
        }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>{headerTitle}</h3>
          <p style={{ fontSize: '0.8rem', opacity: 0.9, margin: '4px 0 0' }}>{headerSubtitle}</p>
        </div>
        <div style={{ padding: '16px' }}>
          {customContent || (
            <>
              {cards.map((card, i) => (
                <MiniCard key={i} {...card} />
              ))}
              {footer && (
                <div style={{ padding: '12px', background: COLORS.bg, borderRadius: '10px', fontSize: '0.8rem', color: COLORS.softGreen, textAlign: 'center', fontWeight: 600 }}>
                  {footer.text}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function MiniCard({ icon, title, subtitle, subtitleColor, done }) {
  return (
    <div style={{
      background: COLORS.bg,
      borderRadius: '10px',
      padding: '12px',
      marginBottom: '10px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontSize: '0.85rem',
      opacity: done ? 0.6 : 1,
      borderLeft: `3px solid ${COLORS.teal}`
    }}>
      {icon && (
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: '8px',
          background: COLORS.white,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1rem',
          flexShrink: 0
        }}>
          {icon}
        </div>
      )}
      <div>
        <strong style={{ display: 'block', fontSize: '0.85rem', textDecoration: done ? 'line-through' : 'none', color: COLORS.darkText }}>{title}</strong>
        <span style={{ fontSize: '0.75rem', color: subtitleColor || COLORS.textLight }}>{subtitle}</span>
      </div>
    </div>
  );
}

export default FeaturesPage;